/**
 * Wave A — `usePermissions()`.
 *
 * Fetches the calling user's effective permissions from the Organizations
 * service and exposes a fast `has()` lookup. Backed by TanStack Query.
 *
 * @module shared/hooks/usePermissions
 */
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getOrganizationsClient } from '../../api/wave-a/client';
import type { MyPermission } from '../../api/wave-a/types';
import { isLegacyMode } from '../rbac/mode';

export const ME_PERMISSIONS_QUERY_KEY = ['me', 'permissions'] as const;

/** Stable cache-key for the active tenant (read once per render). */
function readActiveTenant(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('janus.activeTenant');
}

export interface UsePermissionsResult {
  permissions: MyPermission[];
  loading: boolean;
  error: Error | null;
  /** Returns true iff the user has `permission`, optionally scoped to `resourceId`. */
  has: (permission: string, resourceId?: string) => boolean;
  /** Forces a refetch (also clears the in-process client cache). */
  refetch: () => Promise<void>;
}

/**
 * Hook returning the user's resolved permissions plus a memoized `has()` index.
 *
 * Behavior:
 * - In `legacy` mode this hook short-circuits and returns an empty list with
 *   `has(...) === false` so callers fail-closed; consumers should pair it with
 *   the legacy guard until cutover.
 * - `staleTime: 60_000` (1 min). `gcTime: 5 * 60_000`.
 * - `refetchOnWindowFocus: true`, `refetchOnReconnect: true`.
 * - Query key includes the active tenant id so switching tenants forces a
 *   refetch with the new `X-Active-Tenant` header.
 */
export function usePermissions(options: { enabled?: boolean } = {}): UsePermissionsResult {
  const queryClient = useQueryClient();
  const tenantId = readActiveTenant();
  const enabled = !isLegacyMode() && options.enabled !== false;

  const query = useQuery({
    queryKey: [...ME_PERMISSIONS_QUERY_KEY, tenantId],
    queryFn: async () => {
      const client = getOrganizationsClient();
      const res = await client.getMyPermissions();
      return res.permissions;
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  const permissions = useMemo<MyPermission[]>(
    () => (enabled ? query.data ?? [] : []),
    [enabled, query.data],
  );

  const index = useMemo<Map<string, true>>(() => {
    const m = new Map<string, true>();
    for (const p of permissions) {
      // Resource-scoped key.
      m.set(`${p.permission}|${p.resource_id}`, true);
      // Wildcard fallback so `has(perm)` (no resourceId) returns true if the
      // user has the permission anywhere.
      m.set(`${p.permission}|*`, true);
    }
    return m;
  }, [permissions]);

  const has = useMemo(
    () => (permission: string, resourceId?: string) => {
      if (!enabled) return false;
      if (query.isLoading) return false; // fail-closed while loading
      if (resourceId) return index.has(`${permission}|${resourceId}`);
      return index.has(`${permission}|*`);
    },
    [enabled, index, query.isLoading],
  );

  const refetch = async () => {
    // Clear the in-process client cache so the network round-trip actually fires.
    try {
      getOrganizationsClient().clearMyPermissionsCache();
    } catch {
      // never throw from refetch
    }
    await queryClient.invalidateQueries({ queryKey: ME_PERMISSIONS_QUERY_KEY });
  };

  return {
    permissions,
    loading: enabled ? query.isLoading : false,
    error: (query.error as Error | null) ?? null,
    has,
    refetch,
  };
}
